/**
 * English relative time for UI (notifications, activity feeds).
 * Buckets: “just now” (≤30s) → seconds → minutes → hours → days (≤7) → weeks → months → years.
 */
export function formatTimeAgo(
	input: Date | string | number,
	nowInput: Date | string | number = new Date(),
): string {
	const then = toMs(input);
	const now = toMs(nowInput);
	const diffMs = Math.max(0, now - then);
	const diffSec = Math.floor(diffMs / 1000);

	if (diffSec <= 30) {
		return "just now";
	}

	if (diffSec < 60) {
		return `${diffSec} secs ago`;
	}

	const diffMin = Math.floor(diffSec / 60);
	if (diffMin < 60) {
		return diffMin === 1 ? "1 min ago" : `${diffMin} mins ago`;
	}

	const diffHr = Math.floor(diffSec / 3600);
	if (diffHr < 24) {
		return diffHr === 1 ? "1 hour ago" : `${diffHr} hours ago`;
	}

	const diffDays = Math.floor(diffSec / 86400);
	if (diffDays < 7) {
		return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
	}

	if (diffDays < 30) {
		const w = Math.floor(diffDays / 7);
		const weeks = Math.max(1, w);
		return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
	}

	if (diffDays < 365) {
		const months = Math.floor(diffDays / 30);
		const m = Math.max(1, months);
		return m === 1 ? "1 month ago" : `${m} months ago`;
	}

	const years = Math.floor(diffDays / 365);
	const y = Math.max(1, years);
	return y === 1 ? "1 year ago" : `${y} years ago`;
}

function toMs(v: Date | string | number): number {
	if (v instanceof Date) {
		return v.getTime();
	}
	if (typeof v === "number") {
		return v;
	}
	const t = Date.parse(v);
	if (Number.isNaN(t)) {
		return Date.now();
	}
	return t;
}
